import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import {
  addSfFunnel,
  removeFunnel,
  setFunnelMap,
} from './united-canvas/unitedCanvasReducer';
import { TemplateInterface } from '../components/pages/funnel-map-flow/templates/templatesTypes';
import {
  TransientFunnel,
  PlacingTemplate,
} from '../components/funnel-map-canvas/funnelMapCanvasTypes';
import { FunnelType } from '../types/enums/FunnelType';

export interface CanvasPageState {
  usedInFunnelMapsDialog?: {
    mapsList?: PartialFunnelMap[];
    isLoading: boolean;
    isOpen: boolean;
  };
  transientFunnel?: TransientFunnel;
  placingTemplate?: PlacingTemplate;
  hasUnsavedChanges?: boolean;
  isCreateOrAddDialogOpen?: boolean;
  isPageLoading?: boolean;
  hasFetchingErrors?: boolean;
  deployment?: FunnelDeployment;
  isFreshlyCreatedFunnelMap?: boolean;
  isLoadingCursor?: boolean;
  isPaywallDialogOpen?: boolean;
}

const initialState: CanvasPageState = {
  isPageLoading: true,
  isPaywallDialogOpen: false,
};

const canvasPageSlice = createSlice({
  name: 'canvasPage',
  initialState,
  reducers: {
    setIsUsedInFunnelMapsDialogOpen: (state) => {
      state.usedInFunnelMapsDialog = {
        isOpen: true,
        isLoading: true,
      };
    },
    setIsPageLoading: (state, action: PayloadAction<{ isPageLoading: boolean }>) => {
      state.isPageLoading = action.payload.isPageLoading;
    },
    setHasFetchingErrors: (state, action: PayloadAction<{ hasErrors: boolean }>) => {
      state.hasFetchingErrors = action.payload.hasErrors;
    },
    closeIsUsedInFunnelMapsDialog: (state) => {
      state.usedInFunnelMapsDialog = undefined;
    },
    setMapsForIsUsedInFunnelMaps: (
      state,
      action: PayloadAction<{
        funnelMaps: PartialFunnelMap[];
      }>,
    ) => {
      state.usedInFunnelMapsDialog = {
        isOpen: true,
        isLoading: false,
        mapsList: action.payload.funnelMaps,
      };
    },
    addPlacingTemplate: (
      state,
      action: PayloadAction<{
        template: TemplateInterface;
        useFieldMapping: boolean;
        crmOrgId: string;
      }>,
    ) => {
      const { template, useFieldMapping, crmOrgId } = action.payload;
      state.placingTemplate = { template, useFieldMapping, crmOrgId };
    },
    removeAllTransientFunnelData: (state) => {
      delete state.transientFunnel;
      delete state.placingTemplate;
    },
    setTransientFunnel: (state, action: PayloadAction<TransientFunnel>) => {
      state.transientFunnel = action.payload;
    },
    setSfNewFunnelTransientDataStage: (
      state,
      action: PayloadAction<{
        stage: SweepStage;
      }>,
    ) => {
      const { stage } = action.payload;
      if (
        state.transientFunnel?.type === FunnelType.SALESFORCE &&
        state.transientFunnel?.importType === 'create'
      ) {
        state.transientFunnel.data.stage = stage;
      }
    },
    dropTransientNewFunnelInPlace: (
      state,
      action: PayloadAction<{ position: FunnelMapPosition; sectionId?: string }>,
    ) => {
      const { position, sectionId } = action.payload;
      if (state.transientFunnel) {
        if (
          state.transientFunnel.type === FunnelType.SALESFORCE ||
          state.transientFunnel.type === FunnelType.THIRD_PARTY
        ) {
          state.transientFunnel.position = position;
          if (state.transientFunnel.importType === 'create') {
            state.transientFunnel.sectionId = sectionId;
          }
        }
        if (state.transientFunnel.type === FunnelType.METADATA_ELEMENT) {
          state.transientFunnel.data.metadataElements.forEach((metadataElement) => {
            metadataElement.position = {
              column: metadataElement.position.column + position.column,
              row: metadataElement.position.row + position.row,
            };
          });
        }
        state.transientFunnel.isPlacingFunnel = false;
      }
    },
    removeNewFunnelTransientData: (state) => {
      delete state.transientFunnel;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<{ hasUnsavedChanges: boolean }>) => {
      state.hasUnsavedChanges = action.payload.hasUnsavedChanges;
    },
    setIsCreateOrAddDialogOpen: (state, action: PayloadAction<{ isOpen: boolean }>) => {
      state.isCreateOrAddDialogOpen = action.payload.isOpen;
    },
    setDeployment: (state, action: PayloadAction<{ deployment: FunnelDeployment }>) => {
      state.deployment = action.payload.deployment;
    },
    setFunnelMapIsFreshlyCreated: (state, action: PayloadAction<boolean>) => {
      state.isFreshlyCreatedFunnelMap = action.payload;
    },
    setIsLoadingCursor: (state, action: PayloadAction<boolean>) => {
      state.isLoadingCursor = action.payload;
    },
    setIsPaywallDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isPaywallDialogOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addSfFunnel.type, (state) => {
      delete state.transientFunnel;
    });
    builder.addCase(removeFunnel.type, (state) => {
      delete state.transientFunnel;
    });
    builder.addCase(setFunnelMap.type, (state) => {
      delete state.hasUnsavedChanges;
    });
  },
});

export const selectIsUsedInFunnelMapsDialogOpen = (state: RootState) =>
  Boolean(state.canvasPage.usedInFunnelMapsDialog?.isOpen);

export const selectMapsFromIsUsedInFunnelMaps = (state: RootState) =>
  state.canvasPage.usedInFunnelMapsDialog?.mapsList;

export const selectIsUsedInFunnelMapsLoading = (state: RootState) =>
  state.canvasPage.usedInFunnelMapsDialog?.isLoading;

export const selectPlacingTemplate = (state: RootState) => state.canvasPage.placingTemplate;

export const selectTransientFunnel = (state: RootState) => state.canvasPage.transientFunnel;

export const selectTransientImportSfFunnel = (state: RootState) => {
  if (
    state.canvasPage.transientFunnel?.type === FunnelType.SALESFORCE &&
    state.canvasPage.transientFunnel?.importType === 'import'
  ) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectTransientCreateSfFunnel = (state: RootState) => {
  if (
    state.canvasPage.transientFunnel?.type === FunnelType.SALESFORCE &&
    state.canvasPage.transientFunnel?.importType === 'create'
  ) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectTransientSfFunnel = (state: RootState) => {
  if (state.canvasPage.transientFunnel?.type === FunnelType.SALESFORCE) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectTransientCreateThirdPartyFunnel = (state: RootState) => {
  if (
    state.canvasPage.transientFunnel?.type === FunnelType.THIRD_PARTY &&
    state.canvasPage.transientFunnel?.importType === 'create'
  ) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectTransientImportThirdPartyFunnel = (state: RootState) => {
  if (
    state.canvasPage.transientFunnel?.type === FunnelType.THIRD_PARTY &&
    state.canvasPage.transientFunnel?.importType === 'import'
  ) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectTransientImportMetadataElement = (state: RootState) => {
  if (
    state.canvasPage.transientFunnel?.type === FunnelType.METADATA_ELEMENT &&
    state.canvasPage.transientFunnel?.importType === 'import'
  ) {
    return state.canvasPage.transientFunnel;
  }
};

export const selectHasUnsavedChanges = (state: RootState) => state.canvasPage.hasUnsavedChanges;

export const selectIsCreateOrAddFunnelDialogOpen = (state: RootState) =>
  state.canvasPage.isCreateOrAddDialogOpen;

export const selectIsPageLoading = (state: RootState) => state.canvasPage.isPageLoading;
export const selectHasFetchingErrors = (state: RootState) => state.canvasPage.hasFetchingErrors;

export const selectDeployment = (state: RootState) => state.canvasPage.deployment;

export const selectIsFreshlyCreatedFunnelMap = (state: RootState) =>
  Boolean(state.canvasPage.isFreshlyCreatedFunnelMap);

export const selectIsLoadingCursor = (state: RootState) =>
  Boolean(state.canvasPage.isLoadingCursor);

export const selectIsPaywallDialogOpen = (state: RootState) =>
  Boolean(state.canvasPage.isPaywallDialogOpen);

export const {
  removeAllTransientFunnelData,
  closeIsUsedInFunnelMapsDialog,
  setIsUsedInFunnelMapsDialogOpen,
  setMapsForIsUsedInFunnelMaps,
  setTransientFunnel,
  removeNewFunnelTransientData,
  dropTransientNewFunnelInPlace,
  setSfNewFunnelTransientDataStage,
  setHasUnsavedChanges,
  setIsCreateOrAddDialogOpen,
  setIsPageLoading,
  setHasFetchingErrors,
  setDeployment,
  setFunnelMapIsFreshlyCreated,
  addPlacingTemplate,
  setIsLoadingCursor,
  setIsPaywallDialogOpen,
} = canvasPageSlice.actions;

export default canvasPageSlice.reducer;
